import { jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import react_image_crop from "react-image-crop";
import { useContainerSize } from "../hooks/useContainerSize.js";
import { Box } from "../Box/index.js";
import "react-image-crop/dist/ReactCrop.css";
import { canvasPreview } from "./canvasPreview.js";
import { useCrop } from "./useCrop.js";
import { useImageLoader } from "./useImageLoader.js";
import { calculateInnerBoxDimensions } from "./calculateInnerBoxDimensions.js";
import { useStyles } from "./ImageCropper.styles.js";
function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) resolve(value);
    else Promise.resolve(value).then(_next, _throw);
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(void 0);
        });
    };
}
const ImageCropper = ({ blob, sizeAspect = 1.78, onCropComplete })=>{
    const imgRef = useRef(null);
    const previewCanvasRef = useRef(null);
    const containerRef = useRef(null);
    const containerSize = useContainerSize(containerRef);
    const { imageUrl, imageSize } = useImageLoader(blob);
    const { crop, completedCrop, setCrop, setCompletedCrop, handleImageLoad } = useCrop();
    const { classes } = useStyles({
        completedCrop
    });
    const toBlob = useCallback((canvas)=>new Promise((resolve)=>{
            canvas.toBlob(resolve);
        }), []);
    const handleCropComplete = useCallback((cropElement, percentCrop)=>_async_to_generator(function*() {
            if (!imgRef.current || !previewCanvasRef.current || !percentCrop || !percentCrop.width || !percentCrop.height) return;
            const { naturalWidth, naturalHeight } = imgRef.current;
            const naturalCrop = {
                unit: 'px',
                x: percentCrop.x / 100 * naturalWidth,
                y: percentCrop.y / 100 * naturalHeight,
                width: percentCrop.width / 100 * naturalWidth,
                height: percentCrop.height / 100 * naturalHeight
            };
            setCompletedCrop(naturalCrop);
            yield canvasPreview(imgRef.current, previewCanvasRef.current, naturalCrop);
            const newBlob = yield toBlob(previewCanvasRef.current);
            if (newBlob && onCropComplete) onCropComplete(newBlob);
        })(), [
        onCropComplete,
        setCompletedCrop,
        toBlob
    ]);
    const [initialCropApplied, setInitialCropApplied] = useState(false);
    useEffect(()=>{
        if (crop && !initialCropApplied && imgRef.current && previewCanvasRef.current) {
            const { naturalWidth, naturalHeight } = imgRef.current;
            const naturalCrop = {
                unit: 'px',
                x: crop.x / 100 * naturalWidth,
                y: crop.y / 100 * naturalHeight,
                width: crop.width / 100 * naturalWidth,
                height: crop.height / 100 * naturalHeight
            };
            setCompletedCrop(naturalCrop);
            canvasPreview(imgRef.current, previewCanvasRef.current, naturalCrop).then(()=>{
                if (!previewCanvasRef.current) return;
                toBlob(previewCanvasRef.current).then((newBlob)=>{
                    if (newBlob && onCropComplete) onCropComplete(newBlob);
                });
            });
            setInitialCropApplied(true);
        }
    }, [
        crop,
        initialCropApplied,
        onCropComplete,
        setCompletedCrop,
        toBlob
    ]);
    const innerBoxDimensions = calculateInnerBoxDimensions(imageSize.width, imageSize.height, containerSize.width, containerSize.height);
    const isImageLoaded = imageUrl && imageSize.width && imageSize.height;
    const handleCropChange = (newCrop, percentCrop)=>{
        setCrop(percentCrop);
    };
    const getMaxCrop = (imageWidth, imageHeight, aspect)=>{
        const imageAspect = imageWidth / imageHeight;
        if (imageAspect > aspect) {
            const cropHeight = 100;
            const cropWidth = aspect * imageHeight / imageWidth * 100;
            return {
                unit: '%',
                x: (100 - cropWidth) / 2,
                y: 0,
                width: cropWidth,
                height: cropHeight
            };
        }
        {
            const cropWidth = 100;
            const cropHeight = imageWidth / aspect / imageHeight * 100;
            return {
                unit: '%',
                x: 0,
                y: (100 - cropHeight) / 2,
                width: cropWidth,
                height: cropHeight
            };
        }
    };
    const handleLoad = (e)=>{
        const { naturalWidth, naturalHeight } = e.currentTarget;
        handleImageLoad(naturalWidth, naturalHeight);
        const maxCrop = getMaxCrop(naturalWidth, naturalHeight, sizeAspect);
        setCrop(maxCrop);
        setInitialCropApplied(false);
    };
    useEffect(()=>{
        if (crop && imageSize.width > 0 && imageSize.height > 0) {
            const maxCrop = getMaxCrop(imageSize.width, imageSize.height, sizeAspect);
            setCrop(maxCrop);
            setInitialCropApplied(false);
        }
    }, [
        sizeAspect
    ]);
    if (!blob) return null;
    return /*#__PURE__*/ jsxs(Box, {
        flex: "1",
        minHeight: "100%",
        width: "100%",
        bgcolor: "bg.greyscale.primary",
        ref: containerRef,
        children: [
            isImageLoaded && /*#__PURE__*/ jsx(Box, {
                width: innerBoxDimensions.width,
                height: innerBoxDimensions.height,
                className: classes.cropContainer,
                testId: "crop-container",
                children: /*#__PURE__*/ jsx(react_image_crop, {
                    crop: crop,
                    onChange: handleCropChange,
                    onComplete: handleCropComplete,
                    aspect: sizeAspect,
                    className: classes.ReactCrop,
                    children: /*#__PURE__*/ jsx("img", {
                        ref: imgRef,
                        alt: "Crop me",
                        src: imageUrl,
                        onLoad: handleLoad,
                        className: classes.cropImage,
                        "data-testid": "crop-image"
                    })
                })
            }),
            /*#__PURE__*/ jsx("canvas", {
                ref: previewCanvasRef,
                className: classes.canvasPreview,
                "data-testid": "crop-canvas"
            })
        ]
    });
};
export { ImageCropper };
